export interface ImplementationPromptSite {
  name: string;
  domain: string;
  allowed_origins: string[];
  timezone: string;
  currency: string;
  public_key: string;
  stripe_connected: boolean;
}

interface ImplementationPromptInput {
  site: ImplementationPromptSite;
  baseUrl: string;
}

export function buildImplementationPrompt({ site, baseUrl }: ImplementationPromptInput): string {
  const script = `<script async src="${baseUrl}/v1/data.js?key=${site.public_key}" data-site-key="${site.public_key}"></script>`;
  const origins = site.allowed_origins.map((origin) => `  - ${origin}`).join('\n');
  const webhook = `${baseUrl}/v1/webhooks/stripe/${site.public_key}`;

  return `Implement DataMachine revenue attribution in the existing codebase for “${site.name}”. Inspect the framework and current architecture first, then make the smallest production-quality integration that fits its conventions. Do not merely explain the steps: implement them, run the relevant checks, and report the files changed.

DATAMACHINE PROJECT CONFIGURATION
- DataMachine base URL: ${baseUrl}
- Project domain: ${site.domain}
- Public site key: ${site.public_key}
- Reporting currency: ${site.currency}
- Reporting timezone: ${site.timezone}
- Stripe connection currently configured in DataMachine: ${site.stripe_connected ? 'yes' : 'no'}
- Allowed browser origins:
${origins}

1. INSTALL THE WEBSITE DATA SCRIPT
Add this script exactly once in the global document <head>, using the framework's supported script/head component when one exists:

${script}

Requirements:
- It must load on every public page and survive client-side navigation without being inserted twice.
- Do not rename the script or its /v1/data submission endpoint.
- Never put a server payment secret in browser code, HTML, public environment variables, logs, tests, or commits.
- If the project has a Content Security Policy, allow ${baseUrl} in both script-src and connect-src. Preserve every existing CSP source.

2. PRESERVE CONSENT AND PRIVACY CONTROLS
- If this project requires analytics consent, load the script only after consent is granted. Alternatively, set data-consent="denied" until the consent flow is ready; denied mode must not create a browser identifier or submit data.
- Add data-respect-dnt="true" if the project promises to honor the browser Do Not Track preference.
- Do not send emails, phone numbers, passwords, tokens, card data, authorization values, or session values as goal properties.
- Do not proxy or disguise the integration in a way that bypasses a user's explicit consent choice.

3. RECORD IMPORTANT PRODUCT ACTIONS
After the script is available, record only meaningful conversion actions. Adapt these examples to real actions already present in the product:

window.datamachine?.track('trial_started', { plan: 'growth' });
window.datamachine?.track('checkout_started', { product: 'example' });

For signed-in users, an opaque internal user ID may be linked after consent:

window.datamachine?.identify('internal-user-id');

Add TypeScript declarations for window.datamachine when needed. Do not use an email address as the user ID.

4. PRESERVE THE ATTRIBUTION ID THROUGH CHECKOUT
Read the browser attribution ID only when checkout starts, then send it to this project's own backend with the checkout request:

const anonymousId = localStorage.getItem('dm_anonymous_id');

Treat that value as untrusted input. The backend should pass it to the payment integration without logging it. Do not expose server credentials to the browser.

5A. STRIPE PAYMENT ATTRIBUTION
Stripe webhook endpoint:
${webhook}

In Stripe, subscribe this endpoint to:
- checkout.session.completed
- payment_intent.succeeded
- charge.refunded
- charge.dispute.created

Store the resulting whsec_ signing secret in the DataMachine Project setup screen, not in this project's frontend. When creating Checkout Sessions or PaymentIntents on the server, attach the attribution ID:

metadata: { dm_anonymous_id: anonymousId },
payment_intent_data: { metadata: { dm_anonymous_id: anonymousId } }

For Checkout Sessions, client_reference_id may also contain the attribution ID. Keep currency aligned with ${site.currency}; DataMachine rejects mismatched currencies.

5B. NON-STRIPE OR DIRECT SERVER PAYMENTS
Use ${baseUrl}/v1/payments from server-side code only. Read the one-time project secret from DATAMACHINE_SITE_SECRET in the project's secret manager. The secret is intentionally not included in this prompt. If it has been lost, rotate it in DataMachine Project setup and update the secret manager.

Send:
- Authorization: Bearer <DATAMACHINE_SITE_SECRET>
- Idempotency-Key: a stable unique payment/order key
- Content-Type: application/json

JSON body contract:
{
  "provider_object_id": "stable-order-or-payment-id",
  "occurred_at": "ISO-8601 timestamp",
  "amount_minor": 9900,
  "currency": "${site.currency}",
  "status": "succeeded",
  "customer_id": "optional-opaque-customer-id",
  "anonymous_id": "the dm_anonymous_id value when available",
  "description": "optional non-sensitive description"
}

amount_minor must be an integer in the currency's minor unit. status may be succeeded, refunded, or disputed. Use negative amount_minor values for refunds/disputes and never retry with a different idempotency key for the same payment fact.

6. VERIFY THE IMPLEMENTATION
- Confirm the browser loads ${baseUrl}/v1/data.js successfully.
- Confirm page views submit to ${baseUrl}/v1/data and return HTTP 202 from an allowed origin.
- Confirm SPA navigation produces one page view per real route change, without duplicates.
- Trigger one clearly named test goal and confirm it appears in DataMachine's Live events view.
- For Stripe, use a signed Stripe test webhook and verify a test payment appears with attribution evidence.
- For direct payments, test server-side submission without printing the secret.
- Run the project's typecheck, tests, lint, and production build as available.
- Finish by listing the files changed, verification performed, and any manual Stripe or consent steps still required.`;
}
